import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogContent, TextField, Typography, } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import BaselineConfig from './BaselineConfig';
import ProgressConfig from './ProgressConfig';
import ResultsView from './ResultsView';
import { useSelector } from 'react-redux';
import { getLandDegradation } from '../../api/FlaskAPI';
import { toast } from 'react-toastify';
import { setResults } from "../../reducers/LandDegradationSlice";
import "./styles.css";

const LandDegradationDialog = ({ open, handleClose }) => {
    const dispatch = useDispatch();
    const [sliderPage, setSliderPage] = useState({
        baseline: true,
        progress: false,
        additionalInfo: false,
        result: false,
    });

    const [loading, setLoading] = useState(false);
    const [additionalInformation, setAdditionalInformation] = useState('');
    const [additionalInformationAr, setAdditionalInformationAr] = useState('');

    const areas = useSelector((state) => state.mapState.areas);
    const results = useSelector((state) => state.landDegradation.results);
    const landDegradationState = useSelector((state) => state.landDegradation);

    const handleAnalyze = async () => {
        if (areas.length === 0) {
            toast.error("Please select an area on the map first");
            return;
        }

        const toastId = toast.loading("Processing Request");

        try {
            setLoading(true);

            // Build baseline configuration with NEW key names
            const baselineConfigData = {
                productivity: {
                    id: landDegradationState.productivityIndex,
                    decimal: landDegradationState.productivityDecimals,
                    land_cover_id: landDegradationState.productivityAdvanced.enabled ? null : landDegradationState.productivityLandCover,
                    fromYear: landDegradationState.productivityYears?.from || 2001,
                    toYear: landDegradationState.productivityYears?.to || 2015,
                    uploaded_land_cover: landDegradationState.productivityAdvanced.enabled,
                    user_unccd_tm: landDegradationState.productivityAdvanced.enabled ? {
                        transformationMatrix: landDegradationState.productivityAdvanced.transformationMatrix
                    } : null
                },
                land_cover: {
                    land_cover_id: landDegradationState.landCoverAdvanced.enabled ? null : landDegradationState.landCoverIndex,
                    fromYear: landDegradationState.landCoverYears?.from || 2001,
                    toYear: landDegradationState.landCoverYears?.to || 2015,
                    uploaded_land_cover: landDegradationState.landCoverAdvanced.enabled,
                    user_unccd_tm: landDegradationState.landCoverAdvanced.enabled ? {
                        transformationMatrix: landDegradationState.landCoverAdvanced.transformationMatrix
                    } : null,
                    custom_land_cover_effect: landDegradationState.landCoverIndicatorMatrix !== null,
                    effects_of_land_cover_change: landDegradationState.landCoverIndicatorMatrix
                },
                soil_organic_carbon: {
                    land_cover_id: landDegradationState.soilOrganicLandCoverAdvanced.enabled ? null : landDegradationState.soilOrganicLandCoverIndex,
                    fromYear: landDegradationState.soilOrganicYears?.from || 2001,
                    toYear: landDegradationState.soilOrganicYears?.to || 2015,
                    uploaded_soc: landDegradationState.soilOrganicAdvanced.enabled,
                    uploaded_land_cover: landDegradationState.soilOrganicLandCoverAdvanced.enabled,
                    user_unccd_tm: landDegradationState.soilOrganicLandCoverAdvanced.enabled ? {
                        transformationMatrix: landDegradationState.soilOrganicLandCoverAdvanced.transformationMatrix
                    } : null
                }
            };

            // Build progress configuration with NEW key names - NO autofill from baseline
            const progressConfigData = {
                productivity: {
                    id: landDegradationState.progressProductivityIndex,
                    decimal: landDegradationState.progressProductivityDecimals || 1,
                    land_cover_id: landDegradationState.progressProductivityAdvanced.enabled ? null : landDegradationState.progressProductivityLandCover,
                    fromYear: landDegradationState.progressYears?.productivity?.from || 2005,
                    toYear: landDegradationState.progressYears?.productivity?.to || 2019,
                    uploaded_land_cover: landDegradationState.progressProductivityAdvanced.enabled,
                    user_unccd_tm: landDegradationState.progressProductivityAdvanced.enabled ? {
                        transformationMatrix: landDegradationState.progressProductivityAdvanced.transformationMatrix
                    } : null
                },
                land_cover: {
                    land_cover_id: landDegradationState.progressLandCoverAdvanced.enabled ? null : landDegradationState.progressLandCoverIndex,
                    fromYear: landDegradationState.progressYears?.landcover?.from || 2015,
                    toYear: landDegradationState.progressYears?.landcover?.to || 2019,
                    uploaded_land_cover: landDegradationState.progressLandCoverAdvanced.enabled,
                    user_unccd_tm: landDegradationState.progressLandCoverAdvanced.enabled ? {
                        transformationMatrix: landDegradationState.progressLandCoverAdvanced.transformationMatrix
                    } : null,
                    custom_land_cover_effect: landDegradationState.progressLandCoverIndicatorMatrix !== null,
                    effects_of_land_cover_change: landDegradationState.progressLandCoverIndicatorMatrix
                },
                soil_organic_carbon: {
                    land_cover_id: landDegradationState.progressSoilOrganicLandCoverAdvanced.enabled ? null : landDegradationState.progressSoilOrganicLandCoverIndex,
                    fromYear: landDegradationState.progressYears?.soilorganic?.from || 2015,
                    toYear: landDegradationState.progressYears?.soilorganic?.to || 2019,
                    uploaded_soc: landDegradationState.progressSoilOrganicAdvanced.enabled,
                    uploaded_land_cover: landDegradationState.progressSoilOrganicLandCoverAdvanced.enabled,
                    user_unccd_tm: landDegradationState.progressSoilOrganicLandCoverAdvanced.enabled ? {
                        transformationMatrix: landDegradationState.progressSoilOrganicLandCoverAdvanced.transformationMatrix
                    } : null
                }
            };

            // ============================================
            // 🔍 DEBUG: Log the configuration data
            // ============================================
            console.group('📤 Land Degradation Request Data');
            
            console.log('🟦 BASELINE CONFIGURATION:');
            console.log('├─ Productivity:', {
                index: baselineConfigData.productivity.id,
                decimals: baselineConfigData.productivity.decimal,
                landCoverId: baselineConfigData.productivity.land_cover_id,
                years: `${baselineConfigData.productivity.fromYear} - ${baselineConfigData.productivity.toYear}`,
                uploadedLandCover: baselineConfigData.productivity.uploaded_land_cover
            });
            console.log('├─ Land Cover:', {
                landCoverId: baselineConfigData.land_cover.land_cover_id,
                years: `${baselineConfigData.land_cover.fromYear} - ${baselineConfigData.land_cover.toYear}`,
                uploadedLandCover: baselineConfigData.land_cover.uploaded_land_cover,
                customEffects: baselineConfigData.land_cover.custom_land_cover_effect
            });
            console.log('└─ Soil Organic Carbon:', {
                landCoverId: baselineConfigData.soil_organic_carbon.land_cover_id,
                years: `${baselineConfigData.soil_organic_carbon.fromYear} - ${baselineConfigData.soil_organic_carbon.toYear}`,
                uploadedSOC: baselineConfigData.soil_organic_carbon.uploaded_soc,
                uploadedLandCover: baselineConfigData.soil_organic_carbon.uploaded_land_cover
            });

            console.log('\n🟩 PROGRESS CONFIGURATION:');
            console.log('├─ Productivity:', {
                index: progressConfigData.productivity.id,
                decimals: progressConfigData.productivity.decimal,
                landCoverId: progressConfigData.productivity.land_cover_id,
                years: `${progressConfigData.productivity.fromYear} - ${progressConfigData.productivity.toYear}`,
                uploadedLandCover: progressConfigData.productivity.uploaded_land_cover
            });
            console.log('├─ Land Cover:', {
                landCoverId: progressConfigData.land_cover.land_cover_id,
                years: `${progressConfigData.land_cover.fromYear} - ${progressConfigData.land_cover.toYear}`,
                uploadedLandCover: progressConfigData.land_cover.uploaded_land_cover,
                customEffects: progressConfigData.land_cover.custom_land_cover_effect
            });
            console.log('└─ Soil Organic Carbon:', {
                landCoverId: progressConfigData.soil_organic_carbon.land_cover_id,
                years: `${progressConfigData.soil_organic_carbon.fromYear} - ${progressConfigData.soil_organic_carbon.toYear}`,
                uploadedSOC: progressConfigData.soil_organic_carbon.uploaded_soc,
                uploadedLandCover: progressConfigData.soil_organic_carbon.uploaded_land_cover
            });

            console.log('\n📋 Additional Info:', {
                english: additionalInformation || '(empty)',
                arabic: additionalInformationAr || '(empty)'
            });

            console.groupEnd();
            // ============================================

            const formData = new FormData();

            formData.append('baseline', JSON.stringify(baselineConfigData));
            formData.append('progress', JSON.stringify(progressConfigData));

            // Add coordinates
            if (Array.isArray(areas[0].coordinates)) {
                formData.append('coordinates', JSON.stringify(areas[0].coordinates));
            } else {
                formData.append('geo', JSON.stringify(areas[0].coordinates));
            }

            formData.append('additional_information', additionalInformation || '');
            formData.append('additional_information_ar', additionalInformationAr || '');

            // Add baseline files ONLY if advanced mode is enabled
            if (baselineConfigData.productivity.uploaded_land_cover) {
                if (landDegradationState.productivityAdvanced.firstLandCover) {
                    const firstBaselineProductivityLandCoverFile = await fetch(landDegradationState.productivityAdvanced.firstLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstBaselineProductivityLandCover.tif', { type: 'image/tiff' }));
                    formData.append('firstBaselineProductivityLandCover', firstBaselineProductivityLandCoverFile);
                }
                if (landDegradationState.productivityAdvanced.lastLandCover) {
                    const lastBaselineProductivityLandCoverFile = await fetch(landDegradationState.productivityAdvanced.lastLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastBaselineProductivityLandCover.tif', { type: 'image/tiff' }));
                    formData.append('lastBaselineProductivityLandCover', lastBaselineProductivityLandCoverFile);
                }
            }

            if (baselineConfigData.land_cover.uploaded_land_cover) {
                if (landDegradationState.landCoverAdvanced.firstLandCover) {
                    const firstBaselineLandCoverFile = await fetch(landDegradationState.landCoverAdvanced.firstLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstBaselineLandCover.tif', { type: 'image/tiff' }));
                    formData.append('firstBaselineLandCover', firstBaselineLandCoverFile);
                }
                if (landDegradationState.landCoverAdvanced.lastLandCover) {
                    const lastBaselineLandCoverFile = await fetch(landDegradationState.landCoverAdvanced.lastLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastBaselineLandCover.tif', { type: 'image/tiff' }));
                    formData.append('lastBaselineLandCover', lastBaselineLandCoverFile);
                }
            }

            if (baselineConfigData.soil_organic_carbon.uploaded_soc) {
                if (landDegradationState.soilOrganicAdvanced.firstSoilOrganic) {
                    const firstBaselineSoilOrganicFile = await fetch(landDegradationState.soilOrganicAdvanced.firstSoilOrganic)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstBaselineSoilOrganic.tif', { type: 'image/tiff' }));
                    formData.append('firstBaselineSoilOrganic', firstBaselineSoilOrganicFile);
                }
                if (landDegradationState.soilOrganicAdvanced.lastSoilOrganic) {
                    const lastBaselineSoilOrganicFile = await fetch(landDegradationState.soilOrganicAdvanced.lastSoilOrganic)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastBaselineSoilOrganic.tif', { type: 'image/tiff' }));
                    formData.append('lastBaselineSoilOrganic', lastBaselineSoilOrganicFile);
                }
            }

            if (baselineConfigData.soil_organic_carbon.uploaded_land_cover) {
                if (landDegradationState.soilOrganicLandCoverAdvanced.firstSoilOrganicLandCover) {
                    const firstBaselineSoilOrganicLandCoverFile = await fetch(landDegradationState.soilOrganicLandCoverAdvanced.firstSoilOrganicLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstBaselineSoilOrganicLandCover.tif', { type: 'image/tiff' }));
                    formData.append('firstBaselineSoilOrganicLandCover', firstBaselineSoilOrganicLandCoverFile);
                }
                if (landDegradationState.soilOrganicLandCoverAdvanced.lastSoilOrganicLandCover) {
                    const lastBaselineSoilOrganicLandCoverFile = await fetch(landDegradationState.soilOrganicLandCoverAdvanced.lastSoilOrganicLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastBaselineSoilOrganicLandCover.tif', { type: 'image/tiff' }));
                    formData.append('lastBaselineSoilOrganicLandCover', lastBaselineSoilOrganicLandCoverFile);
                }
            }

            // Add progress files ONLY if advanced mode is enabled
            if (progressConfigData.productivity.uploaded_land_cover) {
                if (landDegradationState.progressProductivityAdvanced.firstLandCover) {
                    const firstProgressProductivityFile = await fetch(landDegradationState.progressProductivityAdvanced.firstLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstProgressProductivityLandCover.tif', { type: 'image/tiff' }));
                    formData.append('firstProgressProductivityLandCover', firstProgressProductivityFile);
                }
                if (landDegradationState.progressProductivityAdvanced.lastLandCover) {
                    const lastProgressProductivityFile = await fetch(landDegradationState.progressProductivityAdvanced.lastLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastProgressProductivityLandCover.tif', { type: 'image/tiff' }));
                    formData.append('lastProgressProductivityLandCover', lastProgressProductivityFile);
                }
            }

            if (progressConfigData.land_cover.uploaded_land_cover) {
                if (landDegradationState.progressLandCoverAdvanced.firstLandCover) {
                    const firstProgressLandCoverFile = await fetch(landDegradationState.progressLandCoverAdvanced.firstLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstProgressLandCover.tif', { type: 'image/tiff' }));
                    formData.append('firstProgressLandCover', firstProgressLandCoverFile);
                }
                if (landDegradationState.progressLandCoverAdvanced.lastLandCover) {
                    const lastProgressLandCoverFile = await fetch(landDegradationState.progressLandCoverAdvanced.lastLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastProgressLandCover.tif', { type: 'image/tiff' }));
                    formData.append('lastProgressLandCover', lastProgressLandCoverFile);
                }
            }

            if (progressConfigData.soil_organic_carbon.uploaded_soc) {
                if (landDegradationState.progressSoilOrganicAdvanced.firstSoilOrganic) {
                    const firstProgressSoilOrganicFile = await fetch(landDegradationState.progressSoilOrganicAdvanced.firstSoilOrganic)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstProgressSoilOrganic.tif', { type: 'image/tiff' }));
                    formData.append('firstProgressSoilOrganic', firstProgressSoilOrganicFile);
                }
                if (landDegradationState.progressSoilOrganicAdvanced.lastSoilOrganic) {
                    const lastProgressSoilOrganicFile = await fetch(landDegradationState.progressSoilOrganicAdvanced.lastSoilOrganic)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastProgressSoilOrganic.tif', { type: 'image/tiff' }));
                    formData.append('lastProgressSoilOrganic', lastProgressSoilOrganicFile);
                }
            }

            if (progressConfigData.soil_organic_carbon.uploaded_land_cover) {
                if (landDegradationState.progressSoilOrganicLandCoverAdvanced.firstSoilOrganicLandCover) {
                    const firstProgressSoilOrganicLandCoverFile = await fetch(landDegradationState.progressSoilOrganicLandCoverAdvanced.firstSoilOrganicLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'firstProgressSoilOrganicLandCover.tif', { type: 'image/tiff' }));
                    formData.append('firstProgressSoilOrganicLandCover', firstProgressSoilOrganicLandCoverFile);
                }
                if (landDegradationState.progressSoilOrganicLandCoverAdvanced.lastSoilOrganicLandCover) {
                    const lastProgressSoilOrganicLandCoverFile = await fetch(landDegradationState.progressSoilOrganicLandCoverAdvanced.lastSoilOrganicLandCover)
                        .then(res => res.blob())
                        .then(blob => new File([blob], 'lastProgressSoilOrganicLandCover.tif', { type: 'image/tiff' }));
                    formData.append('lastProgressSoilOrganicLandCover', lastProgressSoilOrganicLandCoverFile);
                }
            }

            // ============================================
            // 🔍 DEBUG: Log FormData entries
            // ============================================
            // console.group('📦 FormData Entries');
            // const formDataEntries = [];
            // for (let [key, value] of formData.entries()) {
            //     if (value instanceof File) {
            //         formDataEntries.push({ key, value: `[File: ${value.name}, ${(value.size / 1024).toFixed(2)} KB]` });
            //     } else if (typeof value === 'string' && value.length > 100) {
            //         formDataEntries.push({ key, value: `${value.substring(0, 100)}... [${value.length} chars]` });
            //     } else {
            //         formDataEntries.push({ key, value });
            //     }
            // }
            // console.table(formDataEntries);
            // console.groupEnd();
            // ============================================

            const response = await getLandDegradation(formData);

            if (response.status && response.status === 200) {
                dispatch(setResults(response.data));
                setSliderPage({
                    baseline: false,
                    progress: false,
                    additionalInfo: false,
                    result: true
                });
                toast.update(toastId, {
                    render: "Data fetched successfully",
                    type: "success",
                    isLoading: false,
                    autoClose: 3000
                });
            } else {
                toast.update(toastId, {
                    render: "Failed to analyze land degradation",
                    type: "error",
                    isLoading: false,
                    autoClose: 3000
                });
            }
        } catch (error) {
            console.error("Land Degradation Error:", error.response?.data?.message);
            toast.update(toastId, {
                render: Array.isArray(error.response?.data?.message)
                    ? error.response.data.message.join(', ')
                    : error.response?.data?.message || "Failed to analyze land degradation",
                type: "error",
                isLoading: false,
                autoClose: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRight = () => {
        setSliderPage((prev) => {
            const pages = Object.keys(prev);
            const currentIndex = pages.findIndex((page) => prev[page]);
            if (currentIndex === pages.length - 1) return prev;

            return {
                ...prev,
                [pages[currentIndex]]: false,
                [pages[currentIndex + 1]]: true
            };
        });
    };

    const handleLeft = () => {
        setSliderPage((prev) => {
            const pages = Object.keys(prev);
            const currentIndex = pages.findIndex((page) => prev[page]);
            if (currentIndex === 0) return prev;

            return {
                ...prev,
                [pages[currentIndex]]: false,
                [pages[currentIndex - 1]]: true
            };
        });
    };

    return (
        <Dialog
            open={open}
            fullScreen
            onClose={handleClose}
            PaperProps={{
                sx: {
                    position: "absolute",
                    top: 0,
                    right: 0,
                    height: "100%",
                    width: "100%",
                    margin: "0",
                    textAlign: 'left',
                }
            }}
            className="animated-dialog"
        >
            <DialogContent>
                <CloseIcon
                    sx={{
                        cursor: "pointer",
                        position: "absolute",
                        right: "15px",
                        color: "#329ace",
                    }}
                    onClick={handleClose}
                />

                <div className="slider-navigation-wrapper">
                    <div className="pred-left-arrow" onClick={handleLeft} dir='ltr'>
                        <ArrowBackIosIcon /> <span style={{ marginTop: '10px' }}>Previous</span>
                    </div>
                    <div className="slider-content-area">
                        <div style={{ display: sliderPage.baseline ? 'block' : 'none' }}>
                            <BaselineConfig />
                        </div>
                        <div style={{ display: sliderPage.progress ? 'block' : 'none' }}>
                            <ProgressConfig />
                        </div>
                        {/* Additional Information page */}
                        <div
                            style={{
                                display: sliderPage.additionalInfo ? "block" : "none",
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            className="slider-content-area"
                        >
                            <div style={{ width: '100%', padding: '20px' }} className='land-degradation-config'>
                                <Typography variant="h6" gutterBottom>
                                    Additional Information 
                                </Typography>
                                
                                <TextField
                                    label="Additional Information"
                                    multiline
                                    rows={7}
                                    fullWidth
                                    variant="outlined"
                                    value={additionalInformation}
                                    onChange={(e) => setAdditionalInformation(e.target.value)}
                                    placeholder="Enter any additional information..."
                                    inputProps={{ maxLength: 2000, dir: 'ltr' }}
                                    style={{ marginBottom: '20px' }}
                                    helperText={`${additionalInformation.length}/2000`}
                                />
                                
                                <TextField
                                    label="معلومات إضافية"
                                    multiline
                                    rows={7}
                                    fullWidth
                                    variant="outlined"
                                    value={additionalInformationAr}
                                    onChange={(e) => setAdditionalInformationAr(e.target.value)}
                                    placeholder="أدخل أي معلومات إضافية..."
                                    inputProps={{ maxLength: 2000, dir: 'rtl' }}
                                    style={{ marginBottom: '20px' }}
                                    helperText={`${additionalInformationAr.length}/2000`}
                                />
                            </div>
                        </div>
                        <div style={{ display: sliderPage.result ? 'block' : 'none' }}>
                            <ResultsView
                                results={results}
                                loading={loading}
                                handleSubmit={handleAnalyze}
                            />
                        </div>
                    </div>
                    <div className="pred-right-arrow" onClick={handleRight} dir='ltr'>
                        <span style={{ marginTop: '10px' }}>Next</span> <ArrowForwardIosIcon />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LandDegradationDialog;